import React, { useState, useMemo, useRef, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
} from '@goodday-clone/ui'
import {
  Plus,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  RefreshCw,
  Search,
  List,
  LayoutGrid,
  Bookmark,
  ChevronDown,
  SlidersHorizontal,
  X,
  Check,
  BookOpen,
  ArrowLeft,
  FileSpreadsheet,
} from 'lucide-react'
import { LeadDetailModal, LeadDetailData } from '@/components/sales/lead-detail-modal'
import { AddLeadModal } from '@/components/sales/add-lead-modal'
import { ImportExcelModal } from '@/components/sales/import-excel-modal'
import { api } from '@/api/client'
import { groupServicesByCategory } from '@/shared/constants/services'
import { useLeadStatuses, LeadStatusOption } from '@/shared/hooks/useCrmVocabulary'
import { useCallScript } from '@/features/leads/CallScriptContext'
import { CallScriptPanel } from '@/features/leads/CallScriptPanel'
import { LeadsSubNav } from '@/features/leads/LeadsSubNav'
import { DeletionApprovalsSection } from '@/features/leads/DeletionApprovalsSection'
import { UNASSIGNED } from '@/features/leads/assigneeCards'
import { useLeadFollowupsForList } from '@/features/leads/useLeadFollowupsForList'
import { useOverdueFollowups } from '@/features/leads/useOverdueFollowups'
import { cn, cardClassName } from '@jira-clone/ui'

const FALLBACK_LEAD_STATUSES: LeadStatusOption[] = [
  { id: 'status-new', name: 'New', color: '#64748b' },
  { id: 'status-attempting-contact', name: 'Attempting Contact', color: '#f59e0b' },
  { id: 'status-contacted', name: 'Contacted', color: '#eab308' },
  { id: 'status-in-discussion', name: 'In Discussion', color: '#3b82f6' },
  { id: 'status-qualified', name: 'Qualified', color: '#22c55e', isWon: true },
  { id: 'status-unqualified', name: 'Unqualified', color: '#ef4444', isLost: true },
  { id: 'status-lost', name: 'Lost', color: '#b91c1c', isLost: true },
]

export const normalizeStatus = (str?: string) => (str || '').trim().toLowerCase().replace(/[_\s-]+/g, ' ')

export const isStatusMatch = (leadStatus?: string, stageName?: string) => {
  const normLead = normalizeStatus(leadStatus || 'New')
  const normStage = normalizeStatus(stageName)
  if (!normLead && !normStage) return true
  if (normLead === normStage) return true
  if (normStage === 'new' && (normLead === 'open' || normLead === 'prospect')) return true
  if (normStage === 'in discussion' && normLead === 'discussion') return true
  if (normStage === 'attempting contact' && (normLead === 'attempted' || normLead === 'attempting')) return true
  return false
}

export default function LeadsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { openScript } = useCallScript()
  const { data: leadStatuses = [] } = useLeadStatuses()
  const displayStages = useMemo(() => {
    return leadStatuses.length > 0 ? leadStatuses : FALLBACK_LEAD_STATUSES
  }, [leadStatuses])
  const wonStatusNames = displayStages.filter((s) => s.isWon).map((s) => s.name)
  const statusColorFor = (status?: string) =>
    displayStages.find((s) => isStatusMatch(s.name, status))?.color || '#3b82f6'
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isImportModalOpen, setIsImportModalOpen] = useState(false)
  const [selectedLead, setSelectedLead] = useState<LeadDetailData | null>(null)
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  const [notification, setNotification] = useState<{
    type: 'success' | 'error'
    message: string
  } | null>(null)

  // Filter & View States
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<'table' | 'kanban'>('table')
  // The dashboard's status bar links here with ?status=<name>.
  const [searchParams] = useSearchParams()
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all')
  const [sourceFilter, setSourceFilter] = useState('all')
  const [assigneeFilter, setAssigneeFilter] = useState('all')
  const [pinFilter, setPinFilter] = useState<'all' | 'pinned' | 'unpinned'>('all')
  const [activeView, setActiveView] = useState(searchParams.get('view') || 'all')
  const [isViewsOpen, setIsViewsOpen] = useState(false)
  const viewsDropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const viewParam = searchParams.get('view')
    if (viewParam) setActiveView(viewParam)
    const statusParam = searchParams.get('status')
    if (statusParam) setStatusFilter(statusParam)
  }, [searchParams])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (viewsDropdownRef.current && !viewsDropdownRef.current.contains(e.target as Node)) {
        setIsViewsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const mapRawLead = (l: any, i: number): LeadDetailData => ({
    id: l._id || l.id || `lead-${i}`,
    name: l.name,
    role: l.role || 'Prospect',
    company: l.company || '—',
    contactPerson: l.contactPerson || l.role || l.name,
    email: l.email || '',
    phone: l.phone || '',
    address: l.address || '',
    website: l.website || '',
    mapsUrl: l.mapsUrl || '',
    assignee: l.assignee || 'Unassigned',
    actionRequired: l.actionRequired || '',
    actionRequiredUserId: l.actionRequiredUserId || '',
    source: l.source || 'Website',
    status: l.status || 'New',
    value: typeof l.value === 'number' ? `₹${l.value.toLocaleString()}` : (typeof l.value === 'string' && l.value.startsWith('$') ? l.value.replace('$', '₹') : (l.value || '₹0')),
    createdAt: l.createdAt ? new Date(l.createdAt).toLocaleDateString() : 'Recently',
    interest: l.interest || 'Warm',
    rating: typeof l.rating === 'number' ? l.rating : (l.rating ? parseFloat(l.rating) : null),
    notes: l.notes || '',
    services: l.services || [],
    pinned: Boolean(l.pinned),
    number: `#${String(i + 1).padStart(2, '0')}`,
    organizationId: l.organizationId,
    convertedDealId: l.convertedDealId,
  })

  const { data: leads = [], isLoading, refetch } = useQuery<LeadDetailData[]>({
    queryKey: ['leads'],
    queryFn: async () => {
      const data = await api.get<any>('/leads')
      const rawLeads = Array.isArray(data) ? data : (data?.data || data?.leads || [])
      if (!Array.isArray(rawLeads)) return []
      return rawLeads.map(mapRawLead)
    },
  })


  // Fetch followup summaries for all leads to show overdue indicators
  const leadIds = useMemo(() => leads.map(l => l.id), [leads]);
  const { data: followupSummaries = [] } = useLeadFollowupsForList(leadIds);
  const followupMap = useMemo(() => {
    const map = new Map<string, { hasOverdueFollowups: boolean; pendingCount: number }>();
    followupSummaries.forEach(s => map.set(s.leadId, { hasOverdueFollowups: s.hasOverdueFollowups, pendingCount: s.pendingCount }));
    return map;
  }, [followupSummaries]);

  // Overdue followups dataset used by the table overdue filter
  const { data: overdueRows = [] } = useOverdueFollowups()

  // The people the loaded leads actually belong to, so the filter never offers an empty name.
  // 'Unassigned' is a real choice here — those leads need an owner the most.
  const assigneeOptions = useMemo(() => {
    const counts = new Map<string, number>()
    for (const lead of leads) {
      const name = (lead.assignee || UNASSIGNED).trim() || UNASSIGNED
      counts.set(name, (counts.get(name) || 0) + 1)
    }
    return Array.from(counts, ([name, count]) => ({ name, count })).sort((a, b) =>
      a.name === UNASSIGNED ? 1 : b.name === UNASSIGNED ? -1 : a.name.localeCompare(b.name)
    )
  }, [leads])

  const matchesAssignee = (lead: LeadDetailData) =>
    assigneeFilter === 'all' || (lead.assignee || UNASSIGNED).trim() === assigneeFilter

  // Filtered Leads (for Table View and stats)
  const filteredLeads = useMemo(() => {
    return [...leads].sort((a, b) => Number(b.pinned) - Number(a.pinned)).filter((lead) => {
      // Search
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim()
        const matchName = lead.name?.toLowerCase().includes(q)
        const matchCompany = lead.company?.toLowerCase().includes(q)
        const matchEmail = lead.email?.toLowerCase().includes(q)
        const matchContact = lead.contactPerson?.toLowerCase().includes(q)
        const matchPhone = lead.phone?.toLowerCase().includes(q)
        if (!matchName && !matchCompany && !matchEmail && !matchContact && !matchPhone) {
          return false
        }
      }

      // Status
      if (statusFilter !== 'all') {
        if (!isStatusMatch(lead.status, statusFilter)) {
          return false
        }
      }

      // Source
      if (sourceFilter !== 'all') {
        if (lead.source?.toLowerCase() !== sourceFilter.toLowerCase()) {
          return false
        }
      }

      // Assignee
      if (!matchesAssignee(lead)) {
        return false
      }

      // Pin
      if (pinFilter === 'pinned' && !lead.pinned) {
        return false
      }
      if (pinFilter === 'unpinned' && lead.pinned) {
        return false
      }

      // Preset Views
      if (activeView === 'pinned') {
        if (!lead.pinned) return false
      } else if (activeView === 'overdue') {
        const overdueLeadIds = new Set(overdueRows.map((r) => r.lead._id))
        if (!overdueLeadIds.has(lead.id) && !followupMap.get(lead.id)?.hasOverdueFollowups) return false
      } else if (activeView === 'qualified') {
        if (!wonStatusNames.some((w) => isStatusMatch(lead.status, w))) return false
      } else if (activeView === 'high-value') {
        const numVal = parseFloat(String(lead.value).replace(/[^0-9.]/g, '')) || 0
        if (numVal < 25000) return false
      } else if (activeView === 'hot') {
        if (lead.interest?.toLowerCase() !== 'hot') return false
      } else if (activeView === 'inbound') {
        if (lead.source?.toLowerCase() !== 'website') return false
      }

      return true
    })
  }, [leads, searchQuery, statusFilter, sourceFilter, assigneeFilter, pinFilter, activeView, wonStatusNames, overdueRows, followupMap])

  // Base leads for Kanban View (columns represent statuses; search, source, pin, and view presets apply)
  const kanbanBaseLeads = useMemo(() => {
    return [...leads].sort((a, b) => Number(b.pinned) - Number(a.pinned)).filter((lead) => {
      // Search
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim()
        const matchName = lead.name?.toLowerCase().includes(q)
        const matchCompany = lead.company?.toLowerCase().includes(q)
        const matchEmail = lead.email?.toLowerCase().includes(q)
        const matchContact = lead.contactPerson?.toLowerCase().includes(q)
        const matchPhone = lead.phone?.toLowerCase().includes(q)
        if (!matchName && !matchCompany && !matchEmail && !matchContact && !matchPhone) {
          return false
        }
      }

      // Source
      if (sourceFilter !== 'all') {
        if (lead.source?.toLowerCase() !== sourceFilter.toLowerCase()) {
          return false
        }
      }

      // Assignee
      if (!matchesAssignee(lead)) {
        return false
      }

      // Pin
      if (pinFilter === 'pinned' && !lead.pinned) {
        return false
      }
      if (pinFilter === 'unpinned' && lead.pinned) {
        return false
      }

      // Preset Views
      if (activeView === 'pinned') {
        if (!lead.pinned) return false
      } else if (activeView === 'overdue') {
        const overdueLeadIds = new Set(overdueRows.map((r) => r.lead._id))
        if (!overdueLeadIds.has(lead.id) && !followupMap.get(lead.id)?.hasOverdueFollowups) return false
      } else if (activeView === 'qualified') {
        if (!wonStatusNames.some((w) => isStatusMatch(lead.status, w))) return false
      } else if (activeView === 'high-value') {
        const numVal = parseFloat(String(lead.value).replace(/[^0-9.]/g, '')) || 0
        if (numVal < 25000) return false
      } else if (activeView === 'hot') {
        if (lead.interest?.toLowerCase() !== 'hot') return false
      } else if (activeView === 'inbound') {
        if (lead.source?.toLowerCase() !== 'website') return false
      }

      return true
    })
  }, [leads, searchQuery, sourceFilter, assigneeFilter, pinFilter, activeView, wonStatusNames, overdueRows, followupMap])

  const kanbanStages = useMemo(() => {
    const stages = [...displayStages]
    const unmatched = kanbanBaseLeads.filter(
      (l) => !stages.some((s) => isStatusMatch(l.status, s.name))
    )
    if (unmatched.length > 0) {
      stages.push({
        id: 'status-other',
        name: 'Other',
        color: '#94a3b8',
      })
    }
    return stages
  }, [displayStages, kanbanBaseLeads])

  const hasActiveFilters = Boolean(
    searchQuery || statusFilter !== 'all' || sourceFilter !== 'all' || assigneeFilter !== 'all' || pinFilter !== 'all' || activeView !== 'all'
  )

  const handleClearFilters = () => {
    setSearchQuery('')
    setStatusFilter('all')
    setSourceFilter('all')
    setAssigneeFilter('all')
    setPinFilter('all')
    setActiveView('all')
  }

  const handleLeadClick = (lead: LeadDetailData) => {
    setSelectedLead(lead)
    setIsDetailModalOpen(true)
  }

  // Fetched directly rather than found in the already-loaded/filtered list,
  // since the target lead may not be on the current page or may be excluded by
  // whatever filter is currently active.
  const openLeadFromId = (leadId: string) => {
    api.get<any>(`/leads/${leadId}`).then((res) => {
      const raw = res?.data || res
      if (raw && raw._id) {
        handleLeadClick(mapRawLead(raw, 0))
      } else {
        toast.error('Could not open that lead — it may not be assigned to you.')
      }
    }).catch((err: any) => {
      console.error('Failed to load lead for deep-link:', err)
      toast.error(err?.response?.data?.message || 'Could not open that lead')
    })
  }

  // Deep-link from a notification click (?leadId=<id>, see AppLayout.tsx)
  useEffect(() => {
    const leadId = searchParams.get('leadId')
    if (!leadId) return
    openLeadFromId(leadId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const handleLeadUpdate = (updatedLead: LeadDetailData) => {
    queryClient.invalidateQueries({ queryKey: ['leads'] })
    setSelectedLead(updatedLead)
  }

  const handleLeadCreated = (newLead: LeadDetailData) => {
    queryClient.invalidateQueries({ queryKey: ['leads'] })
    setNotification({
      type: 'success',
      message: `Lead "${newLead.name}" successfully created and saved to database!`,
    })
    setTimeout(() => setNotification(null), 5000)
  }

  const handleImported = (result: { imported: number }) => {
    queryClient.invalidateQueries({ queryKey: ['leads'] })
    setNotification({
      type: 'success',
      message: `${result.imported} lead${result.imported === 1 ? '' : 's'} added from the spreadsheet.`,
    })
    setTimeout(() => setNotification(null), 5000)
  }

  return (
    <div className="space-y-6 max-w-full mx-auto">
      {notification && (
        <div
          className={`p-3 rounded-lg border flex items-center justify-between text-xs transition-all ${
            notification.type === 'success'
              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300'
              : 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300'
          }`}
        >
          <div className="flex items-center gap-2">
            {notification.type === 'success' ? (
              <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
            ) : (
              <AlertCircle className="h-4 w-4 shrink-0 text-amber-500" />
            )}
            <span>{notification.message}</span>
          </div>
          <button
            onClick={() => setNotification(null)}
            className="text-muted-foreground hover:text-foreground ml-4"
          >
            ✕
          </button>
        </div>
      )}

      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-[#6825F5] to-[#7C3AED] text-white shadow-sm shadow-purple-200 transition-all hover:shadow-md hover:shadow-purple-300/50"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Leads</h1>
            <p className="text-sm text-muted-foreground">Manage and track prospects through your acquisition funnel</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => openScript(null, 'Prospect')}
            className="gap-1.5 shadow-xs border-indigo-200 text-indigo-700 bg-indigo-50/50 hover:bg-indigo-100 dark:border-indigo-800 dark:text-indigo-300 dark:bg-indigo-950/30 font-semibold"
            title="Open 15-Industry Sales Playbook & Call Scripts"
          >
            <BookOpen className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
            <span>Call Scripts</span>
          </Button>
          <Button size="sm" variant="outline" onClick={() => setIsImportModalOpen(true)} className="gap-1.5 shadow-xs">
            <FileSpreadsheet className="h-4 w-4" /> Import Excel
          </Button>
          <Button size="sm" onClick={() => setIsAddModalOpen(true)} className="gap-1.5 shadow-xs">
            <Plus className="h-4 w-4 mr-1" /> Add Lead
          </Button>
        </div>
      </div>

      <LeadsSubNav current="all" />

      {/* Superadmin only - the component hides itself for every other role */}
      <DeletionApprovalsSection onOpenLead={openLeadFromId} />

      {/* Filter Toolbar (Search, Table/Kanban Switcher, Views Dropdown, All statuses, All sources, All assignees) */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 pt-1">
        {/* Left Side: Search + View Switcher + Saved Views */}
        <div className="flex items-center gap-2.5 flex-wrap flex-1">
          {/* Search Box */}
          <div className="relative w-full sm:w-72 md:w-80">
            <Search className="h-3.5 w-3.5 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search leads by name, company, email..."
              className="w-full pl-9 pr-7 py-1.5 text-xs rounded-full border border-border/80 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all shadow-2xs"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>

          {/* View Mode Switcher (Table / Kanban) */}
          <div className="inline-flex items-center rounded-full bg-muted/40 p-1 border border-border/60 shadow-2xs">
            <button
              type="button"
              onClick={() => setViewMode('table')}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                viewMode === 'table'
                  ? 'bg-[#5b4df2] text-white shadow-xs'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <List className="h-3.5 w-3.5" />
              <span>Table</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('kanban')}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                viewMode === 'kanban'
                  ? 'bg-[#5b4df2] text-white shadow-xs'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <LayoutGrid className="h-3.5 w-3.5" />
              <span>Kanban</span>
            </button>
          </div>

          {/* Overdue Quick Filter Button */}
          {overdueRows.length > 0 && (
            <button
              type="button"
              onClick={() => setActiveView(activeView === 'overdue' ? 'all' : 'overdue')}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all border ${
                activeView === 'overdue'
                  ? 'border-red-500 bg-red-500/10 text-red-600 dark:text-red-400 ring-1 ring-red-500/30'
                  : 'border-red-200 dark:border-red-900/50 bg-red-50/60 dark:bg-red-950/20 text-red-600 dark:text-red-400 hover:bg-red-100/60'
              }`}
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              <span>Overdue ({overdueRows.length})</span>
            </button>
          )}

          {/* Views Preset Dropdown */}
          <div className="relative" ref={viewsDropdownRef}>
            <button
              type="button"
              onClick={() => setIsViewsOpen((prev) => !prev)}
              className={`inline-flex items-center gap-1.5 rounded-full border border-border/80 bg-background px-3.5 py-1.5 text-xs font-medium text-foreground hover:bg-muted/40 transition-colors shadow-2xs ${
                activeView !== 'all' ? 'border-primary text-primary ring-1 ring-primary/20' : ''
              }`}
            >
              <Bookmark className="h-3.5 w-3.5 text-muted-foreground" />
              <span>Views</span>
              <span className="text-[11px] text-muted-foreground font-mono">
                {activeView === 'all' ? '1' : '•'}
              </span>
              <ChevronDown
                className={`h-3 w-3 text-muted-foreground transition-transform ${
                  isViewsOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {isViewsOpen && (
              <div className="absolute left-0 mt-1.5 w-52 rounded-xl border border-border/80 bg-popover p-1.5 shadow-lg z-30 text-xs animate-in fade-in-50 zoom-in-95">
                <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                  Saved Views
                </div>
                {[
                  { id: 'all', label: 'All Leads (Default)' },
                  { id: 'overdue', label: `⚠️ Overdue Follow-ups (${overdueRows.length})` },
                  { id: 'pinned', label: '📌 Pinned Leads' },
                  { id: 'qualified', label: 'Qualified Funnel' },
                  { id: 'high-value', label: 'High Value (≥ ₹25k)' },
                  { id: 'hot', label: 'Hot Prospects' },
                  { id: 'inbound', label: 'Website Inbound' },
                ].map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => {
                      setActiveView(v.id)
                      setIsViewsOpen(false)
                    }}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-left transition-colors ${
                      activeView === v.id
                        ? 'bg-primary/10 text-primary font-semibold'
                        : 'hover:bg-muted text-foreground'
                    }`}
                  >
                    <span>{v.label}</span>
                    {activeView === v.id && <Check className="h-3.5 w-3.5" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Side: Filters (Sliders icon + Status + Source + Reset) */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-muted-foreground p-1">
            <SlidersHorizontal className="h-4 w-4" />
          </div>

          {/* All Statuses Filter */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="appearance-none rounded-full border border-border/80 bg-background pl-3.5 pr-8 py-1.5 text-xs font-medium text-foreground shadow-2xs focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all cursor-pointer"
            >
              <option value="all">All statuses</option>
              {/* The organization's configured statuses (kept selectable if one was deep-linked). */}
              {Array.from(
                new Set([...displayStages.map((st) => st.name), ...(statusFilter !== 'all' ? [statusFilter] : [])])
              ).map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            <ChevronDown className="h-3 w-3 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>

          {/* All Sources Filter */}
          <div className="relative">
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="appearance-none rounded-full border border-border/80 bg-background pl-3.5 pr-8 py-1.5 text-xs font-medium text-foreground shadow-2xs focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all cursor-pointer"
            >
              <option value="all">All sources</option>
              <option value="Website">Website</option>
              <option value="Referral">Referral</option>
              <option value="Trade Show">Trade Show</option>
              <option value="Advertising">Advertising</option>
              <option value="Other">Other</option>
            </select>
            <ChevronDown className="h-3 w-3 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>

          {/* All Assignees Filter */}
          <div className="relative">
            <select
              aria-label="Filter by assignee"
              value={assigneeFilter}
              onChange={(e) => setAssigneeFilter(e.target.value)}
              className={`appearance-none rounded-full border bg-background pl-3.5 pr-8 py-1.5 text-xs font-medium shadow-2xs focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all cursor-pointer ${
                assigneeFilter === 'all' ? 'border-border/80 text-foreground' : 'border-primary/80 text-primary font-semibold ring-1 ring-primary/20'
              }`}
            >
              <option value="all">All assignees</option>
              {assigneeOptions.map((a) => (
                <option key={a.name} value={a.name}>
                  {a.name} ({a.count})
                </option>
              ))}
            </select>
            <ChevronDown className="h-3 w-3 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>

          {/* Pin Filter */}
          <div className="relative">
            <select
              value={pinFilter}
              onChange={(e) => setPinFilter(e.target.value as 'all' | 'pinned' | 'unpinned')}
              className={`appearance-none rounded-full border bg-background pl-3.5 pr-8 py-1.5 text-xs font-medium shadow-2xs focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all cursor-pointer ${
                pinFilter === 'pinned'
                  ? 'border-amber-500/80 bg-amber-500/10 text-amber-600 dark:text-amber-400 font-semibold ring-1 ring-amber-500/30'
                  : pinFilter === 'unpinned'
                  ? 'border-primary/80 bg-primary/10 text-primary font-semibold'
                  : 'border-border/80 text-foreground'
              }`}
            >
              <option value="all">Pin: All</option>
              <option value="pinned">📌 Pinned only</option>
              <option value="unpinned">Unpinned only</option>
            </select>
            <ChevronDown className="h-3 w-3 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={handleClearFilters}
              className="text-xs text-muted-foreground hover:text-foreground font-medium underline underline-offset-2 px-1 transition-colors"
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Main View Area: Table or Kanban */}
      {viewMode === 'table' ? (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between py-4 px-6 border-b border-border/50">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <span>
                {activeView === 'overdue' ? 'Overdue Follow-up Leads' : 'All Active Leads'} ({filteredLeads.length})
              </span>
              {activeView === 'overdue' && (
                <Badge variant="outline" className="border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400 text-[10px]">
                  Overdue Filter Active
                </Badge>
              )}
            </CardTitle>
            <Badge variant="outline" className="text-xs">
              {hasActiveFilters ? `Filtered (${filteredLeads.length}/${leads.length})` : 'Active Funnel'}
            </Badge>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted/40 text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b border-border/50">
                  <tr>
                    <th className="px-6 py-3">Lead Name</th>
                    <th className="px-4 py-3">Company</th>
                    <th className="px-4 py-3">Assignee</th>
                    <th className="px-4 py-3">Source</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Value</th>
                    <th className="px-6 py-3 text-right">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {filteredLeads.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center text-muted-foreground">
                        <div className="flex flex-col items-center justify-center gap-2">
                          <SlidersHorizontal className="h-6 w-6 text-muted-foreground/50" />
                          <p className="font-medium text-sm text-foreground">No leads match your filter criteria</p>
                          <p className="text-xs text-muted-foreground">
                            Try adjusting your search terms, status, source, assignee, or pin filters.
                          </p>
                          {hasActiveFilters && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleClearFilters}
                              className="mt-2 text-xs"
                            >
                              Reset All Filters
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredLeads.map((lead) => (
                      <tr
                        key={lead.id}
                        onClick={() => handleLeadClick(lead)}
                        className="hover:bg-muted/50 cursor-pointer transition-colors group"
                      >
                        <td className="px-6 py-3.5">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {lead.pinned && (
                                <span className="text-amber-500" title="Pinned">
                                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5">
                                    <path d="M16 3a1 1 0 0 1 1 1v1.586l2.293 2.293a1 1 0 0 1-.707 1.707H16.5l-.5 6.5a1 1 0 0 1-1.96.18L13.5 10h-3.5l-.5 5.5a1 1 0 0 1-1.96.18L7.5 10H5.414a1 1 0 0 1-.707-1.707L7 6.586V4a1 1 0 0 1 1-1h8Z"/>
                                  </svg>
                                </span>
                              )}
                              <p className="font-semibold text-foreground group-hover:text-primary transition-colors flex items-center gap-2">
                                <span>{lead.name}</span>
                                  {followupMap.get(lead.id)?.hasOverdueFollowups && (
                                    <span
                                      className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0"
                                      title="Overdue follow-up"
                                    />
                                  )}
                                {lead.number && (
                                  <span className="text-[10px] font-mono font-normal text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                    {lead.number}
                                  </span>
                                )}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openScript(lead.industry || 'web_development', lead.name);
                              }}
                              className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-indigo-100 text-indigo-600 dark:hover:bg-indigo-950/50"
                              title="Launch Call Script"
                            >
                              <BookOpen className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <p className="text-xs text-muted-foreground">{lead.email}</p>
                        </td>
                        <td className="px-4 py-3.5">
                          <p className="font-medium text-foreground">{lead.company}</p>
                          {lead.services && lead.services.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {groupServicesByCategory(lead.services).map(({ category, services: svcs }) => (
                                <span
                                  key={category.category}
                                  className={`inline-block text-[10px] px-1.5 py-0.5 rounded font-medium border ${category.badgeClass} max-w-[150px] truncate`}
                                  title={`${category.category}: ${svcs.join(', ')}`}
                                >
                                  <span className="font-bold opacity-80 mr-1">{category.departmentLabel}:</span>
                                  <span>{svcs[0]}{svcs.length > 1 ? ` +${svcs.length - 1}` : ''}</span>
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-2">
                            <span className="h-6 w-6 rounded-full bg-primary/20 text-primary text-[10px] font-bold flex items-center justify-center uppercase">
                              {lead.assignee ? lead.assignee.charAt(0) : 'O'}
                            </span>
                            <span className="text-xs text-muted-foreground">{lead.assignee}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3.5">
                          <Badge variant="secondary" className="text-xs font-normal">
                            {lead.source}
                          </Badge>
                        </td>
                        <td className="px-4 py-3.5">
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border"
                            style={{
                              color: statusColorFor(lead.status),
                              borderColor: `${statusColorFor(lead.status)}4d`,
                              backgroundColor: `${statusColorFor(lead.status)}1a`,
                            }}
                          >
                            {lead.status}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 font-semibold text-foreground">{lead.value}</td>
                        <td className="px-6 py-3.5 text-xs text-muted-foreground text-right">{lead.createdAt || 'Just now'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Kanban View */
        <div className="w-full overflow-x-auto overflow-y-auto max-h-[calc(100vh-250px)] min-h-[500px] pb-4 custom-scrollbar rounded-xl">
          {statusFilter !== 'all' && (
            <div className="flex items-center justify-between px-3.5 py-2 mb-3 rounded-lg bg-primary/10 border border-primary/20 text-xs text-primary">
              <div className="flex items-center gap-2">
                <span className="font-semibold">Filtered by status:</span>
                <span className="px-2 py-0.5 rounded-full bg-primary text-white text-[11px] font-medium">{statusFilter}</span>
              </div>
              <button
                type="button"
                onClick={() => setStatusFilter('all')}
                className="text-xs font-semibold underline hover:no-underline"
              >
                Show all stages
              </button>
            </div>
          )}
          <div className="flex gap-4 items-start min-w-max pb-2">
            {kanbanStages.map((stage) => {
              const stageLeads = kanbanBaseLeads.filter((l) => {
                if (stage.id === 'status-other') {
                  return !displayStages.some((s) => isStatusMatch(l.status, s.name))
                }
                return isStatusMatch(l.status, stage.name)
              })
              const stageTotal = stageLeads.reduce((sum, l) => {
                const val = typeof l.value === 'number' ? l.value : parseFloat(String(l.value).replace(/[^0-9.]/g, '')) || 0
                return sum + val
              }, 0)

              const isHighlighted = statusFilter !== 'all' && isStatusMatch(stage.name, statusFilter)
              const isDimmed = statusFilter !== 'all' && !isStatusMatch(stage.name, statusFilter)

              return (
                <div
                  key={stage.id}
                  className={`w-[300px] shrink-0 rounded-xl border p-3.5 flex flex-col max-h-[calc(100vh-270px)] min-h-[350px] shadow-xs transition-all ${
                    isHighlighted
                      ? 'border-primary ring-2 ring-primary/20 bg-primary/5'
                      : isDimmed
                      ? 'border-border/40 bg-muted/10 opacity-70'
                      : 'border-border/70 bg-muted/20'
                  }`}
                >
                  {/* Column Header */}
                  <div className="shrink-0 space-y-2 border-b border-border/50 pb-2.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: stage.color || '#3b82f6' }}
                        />
                        <span className="text-xs font-bold uppercase tracking-wider text-foreground">
                          {stage.name}
                        </span>
                      </div>
                      <Badge
                        variant={isHighlighted ? 'default' : 'secondary'}
                        className="text-[10px]"
                      >
                        {stageLeads.length}
                      </Badge>
                    </div>
                    <div className="text-lg font-bold text-foreground">
                      ₹{stageTotal.toLocaleString()}
                    </div>
                  </div>

                  {/* Cards List */}
                  <div className="space-y-2.5 overflow-y-auto flex-1 pr-1 custom-scrollbar pt-2.5">
                    {stageLeads.length === 0 ? (
                      <div className="p-4 rounded-xl border border-dashed border-border/60 text-center text-xs text-muted-foreground">
                        No leads in {stage.name}
                      </div>
                    ) : (
                      stageLeads.map((item) => (
                        <div
                          key={item.id}
                          onClick={() => handleLeadClick(item)}
                          className={cn(cardClassName({ interactive: true }), 'p-3.5 hover:border-primary/60 text-xs space-y-2 transition-all cursor-pointer group')}
                        >
                          <div className="flex items-start justify-between gap-1">
                            <p className="font-bold text-sm text-foreground group-hover:text-primary transition-colors line-clamp-1">
                              {item.name}
                            </p>
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openScript(item.industry || 'web_development', item.name);
                                }}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-indigo-100 text-indigo-600 dark:hover:bg-indigo-950/50"
                                title="Launch Call Script"
                              >
                                <BookOpen className="h-3 w-3" />
                              </button>
                              {item.number && (
                                <span className="text-[9px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                  {item.number}
                                </span>
                              )}
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground truncate">{item.company}</p>

                          {item.services && item.services.length > 0 && (
                            <div className="flex flex-wrap gap-1 pt-0.5">
                              {groupServicesByCategory(item.services).map(({ category, services: svcs }) => (
                                <span
                                  key={category.category}
                                  className={`text-[9px] px-1.5 py-0.5 rounded font-medium border ${category.badgeClass} truncate max-w-[140px]`}
                                  title={`${category.category}: ${svcs.join(', ')}`}
                                >
                                  <span className="font-bold opacity-80 mr-1">{category.departmentLabel}:</span>
                                  <span>{svcs[0]}{svcs.length > 1 ? ` +${svcs.length - 1}` : ''}</span>
                                </span>
                              ))}
                            </div>
                          )}

                          <div className="pt-2 border-t border-border/40 flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <span className="h-5 w-5 rounded-full bg-primary/20 text-primary text-[10px] font-bold flex items-center justify-center uppercase">
                                {item.assignee ? item.assignee.charAt(0) : 'U'}
                              </span>
                              <span className="text-[11px] text-muted-foreground truncate max-w-[90px]">
                                {item.assignee}
                              </span>
                            </div>
                            <span className="font-bold text-xs text-emerald-600 dark:text-emerald-400">
                              {item.value}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Lead Detail Modal */}
      <LeadDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        lead={selectedLead}
        onUpdate={handleLeadUpdate}
        onDeleted={() => queryClient.invalidateQueries({ queryKey: ['leads'] })}
        currentSectionName="All Leads"
        currentSectionHref="/app/leads"
      />

      {/* Call Script panel for the "Script" action on this list — the lead
          modal above renders its own copy while it's open, so this one stays
          off in that case to avoid two overlapping panels. */}
      {!isDetailModalOpen && <CallScriptPanel />}

      {/* Add Lead Modal */}
      <AddLeadModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onLeadCreated={handleLeadCreated}
      />

      {/* Import leads from a spreadsheet */}
      <ImportExcelModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        kind="leads"
        onImported={handleImported}
      />
    </div>
  )
}
